import React, { useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import Fab from '@mui/material/Fab'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { Icon } from '@iconify/react'
import { useRouter } from 'next/router'
import { useSelector } from 'react-redux'
import type { RootState } from '@/src/store'
import type { FeaturePhase, FeatureVersion } from '@/src/types/featureExperience'
import { FEATURE_KEYS } from '@/src/constants/featureExperience'
import { FeatureToggle } from '@/src/components/featureExperience/FeatureToggle'
import { useAuth } from '@/src/hooks/useAuth'
import { useFeatureVersion } from '@/src/hooks/useFeatureVersion'
import { useToggleFeature } from '@/src/hooks/useToggleFeature'
import { useTradingHoursRestaurantScopedResolve } from '@/src/hooks/useTradingHoursFeatureExperience'
import { isIframeContext } from '@/src/utils/iframeUtils'
import { sidebarNavigationResolvedDescription } from '@/src/components/featureExperience/sidebarNavigationMessaging'
import {
  TRADING_HOURS_PILOT_SHORT,
  tradingHoursResolvedDescription,
  tradingHoursToggleLabel
} from '@/src/components/featureExperience/tradingHoursFeatureCopy'

function navigationToggleLabel(version: FeatureVersion): string {
  return version === 'next' ? 'Navigation: New' : 'Navigation: Classic'
}

function useTradingHoursRouteReady(): { active: boolean; restaurantId: string | undefined } {
  const router = useRouter()

  const active =
    router.isReady && typeof router.pathname === 'string' && router.pathname.includes('/menu-services/trading-hours')

  const restaurantId = router.query.id as string | undefined

  return {
    active,
    restaurantId
  }
}

function compactCutoverNavigation(formattedCutover: string): string {
  return `Switch until ${formattedCutover}; after that only the new navigation remains.`
}

function compactCutoverTradingHours(formattedCutover: string): string {
  return `Switch until ${formattedCutover}; after that only the new trading hours layout remains.`
}

const toggleChromeSx = {
  '& .MuiTypography-root': { color: 'inherit' },
  '& .MuiTypography-caption': { color: 'rgba(0, 0, 0, 0.65) !important' },
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD204' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FFD204 !important' }
} as const

/** Vuexy-style rail accent — theme `primary.main` is neutral in this app, so we pin a vivid purple for discoverability. */
const FE_PREFERENCES_LAUNCHER_PURPLE = '#7367F0'
const FE_PREFERENCES_LAUNCHER_PURPLE_HOVER = '#5E50EE'
const FE_PREFERENCES_LAUNCHER_ICON = '#FFFFFF'

/** Matches {@link FeatureToggle} switch visibility: pilot uses info strip, not the switch. */
function shouldNudgeClassicExperience(args: {
  sectionActive: boolean
  phase: FeaturePhase
  canToggle: boolean
  isForcedResolution: boolean
  lockAfterOptInToNext: boolean
  currentVersion: FeatureVersion
}): boolean {
  const { sectionActive, phase, canToggle, isForcedResolution, lockAfterOptInToNext, currentVersion } = args

  if (!sectionActive || phase === 'pilot') {
    return false
  }

  if (!canToggle || isForcedResolution) {
    return false
  }

  if (lockAfterOptInToNext && currentVersion === 'next') {
    return false
  }

  return currentVersion === 'classic'
}

/** Minimised: narrow purple strip; hover (pointer) or tap (touch) reveals the full gear. */
const PEEK_PX = 8
const EXPANDED_PX = 56

/**
 * Floating gear opens a right-hand drawer (navigation + trading-hours toggles when applicable).
 */

export const FeatureExperiencePreferencesLauncher: React.FC = () => {
  const theme = useTheme()
  const { user } = useAuth()
  const router = useRouter()
  const feStatus = useSelector((s: RootState) => s.featureExperience.status)
  const iframe = typeof window !== 'undefined' ? isIframeContext() : false
  const pointerHover = useMediaQuery('(hover: hover) and (pointer: fine)', { noSsr: true })
  const [hoverOpen, setHoverOpen] = useState(false)
  const [peeled, setPeeled] = useState(false)

  const { active: tradingHoursRoute, restaurantId } = useTradingHoursRouteReady()
  useTradingHoursRestaurantScopedResolve(tradingHoursRoute ? restaurantId : undefined)

  const sidebarFv = useFeatureVersion(FEATURE_KEYS.SIDEBAR_NAVIGATION)
  const tradingFv = useFeatureVersion(FEATURE_KEYS.TRADING_HOURS)

  const sidebarToggle = useToggleFeature(FEATURE_KEYS.SIDEBAR_NAVIGATION)
  const tradingToggle = useToggleFeature(FEATURE_KEYS.TRADING_HOURS, { restaurantId })

  const sidebarSection =
    Boolean(user) &&
    !(feStatus === 'idle' || feStatus === 'loading') &&
    !sidebarFv.isLoading &&
    sidebarFv.isInRolloutAudience &&
    !sidebarFv.cutoverReached

  const tradingSection =
    Boolean(user) &&
    !(feStatus === 'idle' || feStatus === 'loading') &&
    router.isReady &&
    tradingHoursRoute &&
    Boolean(restaurantId) &&
    !tradingFv.isLoading &&
    tradingFv.isInRolloutAudience &&
    !tradingFv.cutoverReached

  const showFab = Boolean(!iframe && user && (sidebarSection || tradingSection))

  const [drawerOpen, setDrawerOpen] = useState(false)
  const revealed = pointerHover ? hoverOpen : peeled
  const peekW = !pointerHover && !revealed ? 16 : PEEK_PX

  /** Shown when minimised; hidden when the full tab is out so the Fab matches the pre-peek design. */
  const stripW = revealed ? 0 : peekW

  const sidebarCutover =
    sidebarFv.phase === 'available' && sidebarFv.cutoverDate && !sidebarFv.cutoverReached
      ? compactCutoverNavigation(formatCutover(sidebarFv.cutoverDate))
      : null

  const tradingCutover =
    tradingFv.phase === 'available' && tradingFv.cutoverDate && !tradingFv.cutoverReached
      ? compactCutoverTradingHours(formatCutover(tradingFv.cutoverDate))
      : null

  const animateGear = useMemo(
    () =>
      shouldNudgeClassicExperience({
        sectionActive: sidebarSection,
        phase: sidebarFv.phase,
        canToggle: sidebarFv.canToggle,
        isForcedResolution: sidebarFv.isForcedResolution,
        lockAfterOptInToNext: sidebarFv.lockAfterOptInToNext,
        currentVersion: sidebarToggle.currentVersion
      }) ||
      shouldNudgeClassicExperience({
        sectionActive: tradingSection,
        phase: tradingFv.phase,
        canToggle: tradingFv.canToggle,
        isForcedResolution: tradingFv.isForcedResolution,
        lockAfterOptInToNext: tradingFv.lockAfterOptInToNext,
        currentVersion: tradingToggle.currentVersion
      }),
    [
      sidebarSection,
      sidebarFv.phase,
      sidebarFv.canToggle,
      sidebarFv.isForcedResolution,
      sidebarFv.lockAfterOptInToNext,
      sidebarToggle.currentVersion,
      tradingSection,
      tradingFv.phase,
      tradingFv.canToggle,
      tradingFv.isForcedResolution,
      tradingFv.lockAfterOptInToNext,
      tradingToggle.currentVersion
    ]
  )

  if (!showFab) {
    return null
  }

  return (
    <>
      <ClickAwayListener
        onClickAway={() => {
          if (drawerOpen || pointerHover) {
            return
          }
          setPeeled(false)
        }}
      >
        <Tooltip
          title={
            animateGear
              ? 'Experience preferences — you can switch one or more areas to the new version'
              : 'Experience preferences (navigation, trading hours when on that page)'
          }
          placement='left'
        >
          <Box
            data-testid='fe-preferences-launcher'
            onMouseEnter={() => pointerHover && setHoverOpen(true)}
            onMouseLeave={() => pointerHover && setHoverOpen(false)}
            onClick={e => {
              if (pointerHover || peeled) {
                return
              }
              e.stopPropagation()
              setPeeled(true)
            }}
            sx={{
              position: 'fixed',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: theme.zIndex.appBar + 1,
              maxWidth: revealed ? EXPANDED_PX : peekW,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              alignItems: 'center',
              borderRadius: 0,
              transition: t =>
                t.transitions.create('maxWidth', {
                  duration: t.transitions.duration.shorter,
                  easing: t.transitions.easing.easeOut
                }),
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'none'
              }
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                flexDirection: 'row',
                flexShrink: 0,
                alignItems: 'center',
                minHeight: 48
              }}
            >
              <Fab
                aria-label={
                  animateGear ? 'Experience preferences — new version available to try' : 'Experience preferences'
                }
                aria-haspopup='dialog'
                aria-expanded={drawerOpen}
                tabIndex={revealed && !drawerOpen ? 0 : -1}
                onClick={e => {
                  e.stopPropagation()
                  if (!pointerHover && !peeled) {
                    return
                  }
                  setDrawerOpen(o => !o)
                }}
                sx={{
                  width: 'clamp(28px, 3.2vw + 14px, 38px)',
                  height: 'clamp(28px, 3.2vw + 14px, 38px)',
                  minHeight: 'unset',
                  p: 0,
                  borderRadius: 0,
                  borderTopLeftRadius: 8,
                  borderBottomLeftRadius: 8,
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  flexShrink: 0,
                  bgcolor: FE_PREFERENCES_LAUNCHER_PURPLE,
                  color: FE_PREFERENCES_LAUNCHER_ICON,
                  pointerEvents: revealed ? 'auto' : 'none',
                  boxShadow:
                    '-2px 0 12px rgba(0,0,0,0.14), -1px 0 18px rgba(115, 103, 240, 0.45), 0 0 0 1px rgba(255,255,255,0.12) inset',
                  transition: t => t.transitions.create(['background-color', 'box-shadow'], { duration: 180 }),
                  '&:hover': {
                    bgcolor: FE_PREFERENCES_LAUNCHER_PURPLE_HOVER,
                    boxShadow:
                      '-3px 0 14px rgba(0,0,0,0.16), -2px 0 22px rgba(115, 103, 240, 0.55), 0 0 0 1px rgba(255,255,255,0.16) inset'
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${FE_PREFERENCES_LAUNCHER_PURPLE_HOVER}`,
                    outlineOffset: 2
                  },
                  ...(animateGear && {
                    '@keyframes feExperienceGearSpin': {
                      from: { transform: 'rotate(0deg)' },
                      to: { transform: 'rotate(360deg)' }
                    },
                    '& .fe-experience-gear-icon': {
                      display: 'inline-flex',
                      animation: 'feExperienceGearSpin 3s linear infinite',
                      '@media (prefers-reduced-motion: reduce)': {
                        animation: 'none'
                      }
                    }
                  })
                }}
              >
                <Box
                  className='fe-experience-gear-icon'
                  component='span'
                  aria-hidden
                  sx={{
                    display: 'inline-flex',
                    lineHeight: 0,
                    '& svg': {
                      width: 'clamp(14px, 1.6vw + 8px, 18px)',
                      height: 'clamp(14px, 1.6vw + 8px, 18px)'
                    }
                  }}
                >
                  <Icon icon='mdi:cog' />
                </Box>
              </Fab>
              {stripW > 0 ? (
                <Box
                  aria-hidden
                  sx={{
                    display: 'flex',
                    width: stripW,
                    minHeight: 'clamp(28px, 3.2vw + 14px, 38px)',
                    alignSelf: 'center',
                    flexShrink: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: FE_PREFERENCES_LAUNCHER_PURPLE,
                    borderTopLeftRadius: 6,
                    borderBottomLeftRadius: 6,
                    boxShadow: 'inset 1px 0 0 rgba(255,255,255,0.12), -1px 0 10px rgba(115, 103, 240, 0.3)'
                  }}
                >
                  <Icon
                    icon='mdi:chevron-left'
                    style={{
                      color: FE_PREFERENCES_LAUNCHER_ICON,
                      width: stripW < 12 ? 10 : 16,
                      height: stripW < 12 ? 10 : 16,
                      flexShrink: 0
                    }}
                  />
                </Box>
              ) : null}
            </Box>
          </Box>
        </Tooltip>
      </ClickAwayListener>

      <Drawer
        anchor='right'
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: 'min(100vw - 16px, 420px)', sm: 420 },
            maxWidth: '100%',
            borderLeft: theme => `1px solid ${theme.palette.divider}`,
            boxShadow: theme => theme.shadows[8],
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            borderBottom: theme => `1px solid ${theme.palette.divider}`,
            flexShrink: 0
          }}
        >
          <Stack direction='row' alignItems='flex-start' justifyContent='space-between' spacing={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant='h6' sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                Experience preferences
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                Choose classic or new where rollout allows. Close when you&apos;re done — the gear stays available while
                switching is offered.
              </Typography>
            </Box>
            <IconButton
              aria-label='Close experience preferences'
              edge='end'
              size='small'
              onClick={() => setDrawerOpen(false)}
              sx={{ mt: -0.5, flexShrink: 0 }}
            >
              <Icon icon='mdi:close' width={22} height={22} />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 3, py: 2.5 }}>
          {sidebarSection ? (
            <Box sx={{ mb: tradingSection ? 3 : 0 }}>
              <Typography variant='overline' color='text.secondary' sx={{ display: 'block', mb: 1, letterSpacing: 1 }}>
                Navigation
              </Typography>
              {sidebarCutover ? (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ display: 'block', mb: 1.5, lineHeight: 1.45 }}
                >
                  {sidebarCutover}
                </Typography>
              ) : null}
              <Box sx={toggleChromeSx} onClick={e => e.stopPropagation()}>
                <FeatureToggle
                  featureKey={FEATURE_KEYS.SIDEBAR_NAVIGATION}
                  label='New navigation'
                  description='Try the updated sidebar layout.'
                  resolveLabel={navigationToggleLabel}
                  resolveDescription={(v: FeatureVersion) => sidebarNavigationResolvedDescription(v, sidebarFv.phase)}
                />
              </Box>
            </Box>
          ) : null}

          {sidebarSection && tradingSection ? <Divider sx={{ my: 1 }} /> : null}

          {tradingSection ? (
            <Box>
              <Typography variant='overline' color='text.secondary' sx={{ display: 'block', mb: 1, letterSpacing: 1 }}>
                Trading hours layout
              </Typography>
              {tradingCutover ? (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ display: 'block', mb: 1.5, lineHeight: 1.45 }}
                >
                  {tradingCutover}
                </Typography>
              ) : null}
              <Box sx={toggleChromeSx} onClick={e => e.stopPropagation()}>
                <FeatureToggle
                  featureKey={FEATURE_KEYS.TRADING_HOURS}
                  restaurantId={restaurantId}
                  label='New trading hours'
                  description='Try the updated trading hours layout.'
                  resolveLabel={tradingHoursToggleLabel}
                  resolveDescription={(v: FeatureVersion) =>
                    tradingHoursResolvedDescription(v, tradingFv.phase ?? 'available')
                  }
                  pilotPhaseTitle='Trading hours pilot'
                  pilotPhaseDescription={TRADING_HOURS_PILOT_SHORT}
                  optInLockCaption='The new trading hours layout is locked for your account; you cannot switch back to classic.'
                />
              </Box>
            </Box>
          ) : null}
        </Box>
      </Drawer>
    </>
  )
}

function formatCutover(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'long',
    timeStyle: 'short'
  })
}
